import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';

import { distinctUntilChanged, filter, map, merge, startWith, Subject, takeUntil } from 'rxjs';

import { ScanProcessingService, TranslationService, WizardSummaryData } from '@mirage-projects/cw-commons';
import {
    PcvizScanProcessingOptions,
    PcvizScanProjectJobAdapter,
    PcvizWorkspaceScan,
    PcvizWorkspaceStationTypes,
    PcvizWorkspaceStationTypeValues,
} from '@mirage-projects/cw-models';
import { JobPcvizService } from '@mirage-projects/cw-pcviz-core';

import { UploadWizardService } from '../../services/upload-wizard-service/upload-wizard.service';
import { ListSelectionItem } from '../list-selection';
import { WizardFooterService } from '../wizard-footer/wizard-footer.service';
import { ScanProcessingOptionsService } from './scan-processing-options.service';

/**
 * Scan processing options
 * This component is used as an optional step in the import workflow to set the scan processing options
 */
@Component({
    selector: 'cw-scan-processing-options',
    templateUrl: 'scan-processing-options.component.html',
    styleUrls: ['./scan-processing-options.component.scss'],
    providers: [ScanProcessingOptionsService],
    standalone: false,
})
export class ScanProcessingOptionsComponent implements OnInit, OnDestroy, AfterViewInit {
    /** Text that will be shown as a definition of the wizard in all the steps */
    @Input() subtitle = '';
    /** Show colorize and panorama as hidde */
    @Input() isDisabledColorizeAndPanorama = false;
    /** Structure with the summary information that will show the summary component */
    @Input() summaryList!: Partial<WizardSummaryData>[];
    /** Indicates that the summary will process a scan(s) */
    @Input() processScan = false;

    /** List of scans that will be processed */
    @Input()
    set scans(value: PcvizWorkspaceScan[]) {
        this._scans = value;
        this.listItems = this.scans?.map((scan) => ({
            label: scan.file,
            isSelected: scan.selected,
            data: scan,
            isDisabled: scan.isDisabled,
        }));
        this.updateSelectedScanTypes();
    }
    get scans(): PcvizWorkspaceScan[] {
        return this._scans;
    }

    /** This event emits when a change in the validation occurs (change from valid to not valid, for example) */
    @Output() validityChange = new EventEmitter<boolean>();
    /** This event informs when the credit estimation can o cannnot be done*/
    @Output() creditsNotEstimate = new EventEmitter<boolean>();

    listItems: ListSelectionItem[] = [];
    summaryPosition = 0;
    edgeNoiseReductLevels = this.scanProcessingService.edgeNoiseReductLevels;
    panoramaResolution = this.scanProcessingService.panoramaResolution;
    options: PcvizScanProcessingOptions = {
        processPointcloud: true,
        highQualityPointcloud: false,
        processPanorama: true,
        panoramaResX: 8192,
        panoramaResY: 4096,
        panoramaRadius: 20,
        precision: 0.001,
        density: 0.001,
        colorize: true,
        maxProcesses: 1,
        maxDistance: 400.0,
        surfaceSmoothing: true,
        strayFilter: false,
        autodetectTargets: false,
        edgeNoiseReduction: true,
        edgeNoiseReductionLevel: this.edgeNoiseReductLevels[1].value,
        autodetectCheckerboards: false,
        autodetectSpheres: false,
        sphereSizes: ['30', '69.5', '100'],
    };

    private onDestroy$: Subject<void> = new Subject();
    private _scans: PcvizWorkspaceScan[] = [];
    private selectedScanTypes = new Set<PcvizWorkspaceStationTypeValues>();

    constructor(
        public scanProcessingOptionsService: ScanProcessingOptionsService,
        private uploadWizardService: UploadWizardService,
        private translationService: TranslationService,
        private scanProcessingService: ScanProcessingService,
        private jobAdapterService: JobPcvizService,
        private wizardFooterService: WizardFooterService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.init();
        this.listenToStepChanged();
        this.formOnChanges();
        if (this.isSite()) {
            this.initUncheckOnDisabled();
        }
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.emitCurrentValidityStatus();
        });
        this.scanProcessingOptionsService.scanProcessingForm.statusChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
            this.emitCurrentValidityStatus();
        });
    }

    ngOnDestroy(): void {
        this.clearScanSelection();
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    /**
     * Initialize values for the step
     */
    init(): void {
        this.clearScanSelection();
        if (this.isDisabledColorizeAndPanorama) {
            this.options.colorize = false;
            this.options.processPanorama = false;
        }

        this.scanProcessingOptionsService.initScanProcessingForm(this.options);
    }

    formOnChanges(): void {
        this.creditsNotEstimate.emit(true);
        this.scanProcessingOptionsService.scanProcessingForm
            .get('processPointcloud')
            ?.valueChanges.pipe(takeUntil(this.onDestroy$))
            .subscribe((value) => {
                this.creditsNotEstimate.emit(value);
            });
    }

    /**
     * Checks if the user is navigating to/from this step, so the validations and summary can be done
     */
    listenToStepChanged(): void {
        //those two are for the process scan
        if (this.wizardFooterService) {
            this.footerListeners();
        }

        // this is for importing
        if (this.uploadWizardService.stepChanged) {
            this.uploadWizardService.stepChanged
                .pipe(
                    takeUntil(this.onDestroy$),
                    filter((wizardComponent) => {
                        const step = this.translationService.translate('WIZARD.TAB_SCAN_PROCESSING');
                        return this.uploadWizardService.comparePreviousStep(wizardComponent, step);
                    })
                )
                .subscribe((wizardComponent) => {
                    this.summaryPosition = wizardComponent.selectedIndex;
                    this.validate();
                });
        }
    }

    /**
     * Gets the selected scan output formats
     */
    getSelectedFormats(): string[] {
        const selectedFormats = this.scanProcessingService.scanOutputFormats.filter(
            (outputFormat) => (this.options as any)[outputFormat.option]
        );

        return selectedFormats.map((format) => format.name);
    }

    /**
     * Indicates if the user has selected the checkbox to process the pointcloud
     */
    hasToProcessPointcloud(): boolean {
        return this.scanProcessingOptionsService.scanProcessingForm.controls['processPointcloud'].value;
    }

    /** Checks if there is any data to start processing */
    hasDataToProcess(): boolean {
        return this.hasToProcessPointcloud() || this.hasToProcessPanorama();
    }

    /**
     * Indicates if the user has selected the checkbox to process the pointcloud
     */
    hasToProcessPanorama(): boolean {
        return this.scanProcessingOptionsService.scanProcessingForm.controls['processPanorama'].value;
    }

    /**
     * Message to acknowledge the user about the estimation costs
     */
    hasToShowCreditEstimationMessage(): boolean {
        const processPointcloud = this.scanProcessingOptionsService.scanProcessingForm.value.processPointcloud;
        return processPointcloud && !this.uploadWizardService.isSite() && !this.processScan;
    }

    onSelectedElements(items: ListSelectionItem[]): void {
        this.scans.forEach((scan) => (scan.selected = !!items.find((item) => item.data === scan)?.isSelected));
        this.updateSelectedScanTypes();

        this.emitCurrentValidityStatus();

        this.cdr.detectChanges();
    }

    get isEnabledLimitDistance(): boolean {
        return this.getFormValue('maxDistanceEnabled');
    }

    get isEnabledNoiseReductionLevel(): boolean {
        return this.getFormValue('edgeNoiseReduction');
    }

    get isSurfaceSmoothingDisabled(): boolean {
        if (!this.hasToProcessPointcloud()) {
            return true;
        }

        return this.isSite() ? !this.areGlsScansSelected() : false;
    }

    /**
     * Check if the selected scans are GLS type.
     *
     * This method returns always true if the process comes from importing
     */
    areGlsScansSelected(): boolean {
        return this.fromImport || this.selectedScanTypes.has(this.workspaceStationTypes.GLS);
    }

    areCrScansSelected(): boolean {
        const crTypes = [this.workspaceStationTypes.CR];
        const isCR = crTypes.some((type) => this.selectedScanTypes.has(type));
        // Enable always when importing scans
        return this.fromImport || isCR;
    }

    isSite(): boolean {
        return this.uploadWizardService.isSite();
    }

    /**
     * Gets the max distance value by scan station type
     */
    get maxValidDistance(): number {
        return this.areCrScansSelected()
            ? this.scanProcessingOptionsService.CRP1_MAX_DISTANCE
            : this.scanProcessingOptionsService.DEFAULT_MAX_DISTANCE;
    }

    /**
     * This filters are conditionally enabled or disabled, when disabled they should be unchecked
     * Filter: Surface Smoothing, Stray Points Filter and Autodetect Targets
     */
    private initUncheckOnDisabled(): void {
        const form = this.scanProcessingOptionsService.scanProcessingForm;
        const { surfaceSmoothing, strayFilter, autodetectTargets } = form.controls;
        const mappedStatusChanges$ = [surfaceSmoothing, strayFilter, autodetectTargets].map((control) =>
            control.statusChanges.pipe(
                startWith(control.status),
                distinctUntilChanged(),
                map((status) => [control, status])
            )
        );
        const statusDisabled$ = merge(...mappedStatusChanges$).pipe(
            takeUntil(this.onDestroy$),
            filter(([, status]) => status === 'DISABLED')
        );
        statusDisabled$.subscribe(([control]) => {
            (control as FormControl<boolean>).setValue(false);
        });
    }

    private updateSelectedScanTypes(): void {
        this.selectedScanTypes = this.scanProcessingOptionsService.getSelectedScanTypes(this.scans);
    }

    private clearScanSelection(): void {
        if (!this.isSite()) {
            this.scans?.forEach((scan) => (scan.selected = false));
        }
    }

    private validate(): boolean {
        const jobAdapter = this.jobAdapterService.getJobAdapter() as PcvizScanProjectJobAdapter | undefined;

        const scans = this.listItems
            .filter((item) => item.isSelected)
            .map((item) => {
                item.data.selected = item.isSelected;
                return item.data;
            });
        // TODO: Check the valid cases
        if (!scans.length && !this.uploadWizardService.importType) {
            return false;
        }
        if (jobAdapter?.getId() !== this.jobAdapterService.getCrp1ScanProjectJobAdapter().ID) {
            jobAdapter?.setScans(scans);
        }

        const jobAdapterOptions = this.getUpdatedOptions();
        jobAdapterOptions.sphereSizes = jobAdapterOptions.sphereSizes.map((radius) => Number(radius) / 1000) as any;
        jobAdapter?.setOptions(jobAdapterOptions);
        this.updateSummary(scans);

        return true;
    }

    private updateSummary(scans: PcvizWorkspaceScan[]): void {
        const fields = [];

        if (this.getSelectedFormats().length) {
            fields.push({
                key: 'SCANPROC.SCAN_PROCESSING_SELECT_SCAN',
                /* istanbul ignore next*/
                value: scans.map((scan) => scan.directory).join(', '),
            });
            fields.push({
                key: 'SCANPROC.SCAN_PROCESSING_OUTPUTS',
                value: this.getSelectedFormats().join(', '),
            });
            fields.push({
                key: 'SCANPROC.SCAN_PRECISION',
                value: this.getFormValue('precision'),
            });
            fields.push({
                key: 'SCANPROC.SURFACE_SMOOTHING',
                value: this.getFormValue('surfaceSmoothing'),
            });
            if (this.isSite()) {
                fields.push({
                    key: 'SCANPROC.STRAY_POINTS_FILTER',
                    value: this.getFormValue('strayFilter'),
                });
                fields.push({
                    key: 'SCANPROC.AUTODETECT_TARGETS',
                    value: this.getAutodetectTargetsSummary(),
                });
            }
            const noiseLevel = this.getFormValue('edgeNoiseReductionLevel');
            fields.push({
                key: 'SCANPROC.EDGE_NOISE_REDUCT',
                value: this.isEnabledNoiseReductionLevel ? this.scanProcessingService.edgeNoiseReductLevels[noiseLevel].label : 'false',
            });

            if (this.isEnabledLimitDistance) {
                fields.push({
                    key: 'SCANPROC.MAX_DISTANCE',
                    value: this.getFormValue('maxDistanceValue'),
                });
            }
            if (!this.isDisabledColorizeAndPanorama) {
                fields.push({
                    key: 'SCANPROC.COLORIZE',
                    value: this.getFormValue('colorize'),
                });

                if (this.getFormValue('processPanorama')) {
                    const resolution = this.getFormValue('resolution');
                    fields.push({
                        key: 'SCANPROC.SUMMARY_PANORAMA_RESOLUTION',
                        value: `${resolution.panoramaResX}x${resolution.panoramaResY}`,
                    });

                    fields.push({
                        key: 'SCANPROC.SUMMARY_PANORAMA_RADIUS',
                        value: this.getFormValue('radius'),
                    });
                }
            }
        }

        const summary = this.summaryList ?? this.uploadWizardService.summary;

        summary[this.summaryPosition] = {
            title: 'SCANPROC.SCAN_PROCESSING_OPTIONS',
            notice: fields.length ? 'SCANPROC.SCAN_PROCESSING_SUMMARY' : 'SCANPROC.UNSELECTED_SCAN_PROCESSING_SUMMARY',
            fields,
        };
    }

    private getAutodetectTargetsSummary(): string {
        if (!this.options.autodetectTargets) {
            return this.translationService.translate(`GENERAL.${this.options.autodetectTargets.toString().toUpperCase()}`);
        }
        let autodetectSummary = '';
        if (this.options.autodetectCheckerboards) {
            autodetectSummary = autodetectSummary.concat(this.translationService.translate('FEATURE_PROPERTIES.Checkerboard'));
        }
        if (this.options.autodetectSpheres) {
            // Check again checkerboards to know if we need to add a comma character
            if (this.options.autodetectCheckerboards) {
                autodetectSummary = autodetectSummary.concat(', ');
            }
            autodetectSummary = autodetectSummary.concat(this.translationService.translate('FEATURE_PROPERTIES.Sphere'));
            const unit = this.translationService.translate('UNIT_SERVICE.MM');
            for (const size of this.options.sphereSizes) {
                autodetectSummary = autodetectSummary.concat(` (${size}${unit})`);
            }
        }
        return autodetectSummary ? autodetectSummary : this.translationService.translate('GENERAL.FALSE');
    }

    private getFormValue(name: string): any {
        return this.scanProcessingOptionsService.scanProcessingForm.get(name)?.value;
    }

    /* istanbul ignore next*/
    private isCurrentStep(stepLabel: string): boolean {
        return stepLabel === this.translationService.translate('WIZARD.TAB_SCAN_PROCESSING');
    }

    private emitCurrentValidityStatus(): void {
        const formValid = this.scanProcessingOptionsService.scanProcessingForm.status === 'VALID';
        const scansSelected = this.listItems?.length ? this.listItems.some((scan) => scan.isSelected) : true;

        this.validityChange.emit(formValid && scansSelected && this.hasDataToProcess());
    }

    private footerListeners(): void {
        this.wizardFooterService
            .onNextButtonClick()
            .pipe(
                takeUntil(this.onDestroy$),
                filter((wizardComponent) => {
                    return this.isCurrentStep(wizardComponent.selectedItem.label);
                })
            )
            .subscribe((wizardComponent) => {
                this.summaryPosition = wizardComponent.selectedIndex;
                this.validate();

                wizardComponent.nextStep();
            });

        this.wizardFooterService
            .onPrevButtonClick()
            .pipe(
                takeUntil(this.onDestroy$),
                filter((wizardComponent) => {
                    return this.isCurrentStep(wizardComponent.selectedItem.label);
                })
            )
            .subscribe((wizardComponent) => {
                wizardComponent.prevStep();
            });
    }

    private getUpdatedOptions(): PcvizScanProcessingOptions {
        // It is necessary to check hardwareConcurrency because Safari doesn't return value
        if (this.uploadWizardService.isSite() && !this.options.highQualityPointcloud && navigator.hardwareConcurrency) {
            this.options.maxProcesses = Math.max(1, navigator.hardwareConcurrency - 2);
        }

        this.options.processPointcloud = this.getFormValue('processPointcloud');
        this.options.processPanorama = this.getFormValue('processPanorama');
        this.options.density = this.getFormValue('density');
        this.options.precision = this.getFormValue('precision');
        this.options.edgeNoiseReduction = this.getFormValue('edgeNoiseReduction');
        this.options.edgeNoiseReductionLevel = this.getFormValue('edgeNoiseReductionLevel');
        this.options.surfaceSmoothing = this.getFormValue('surfaceSmoothing');
        this.options.strayFilter = this.getFormValue('strayFilter');
        this.options.autodetectTargets = this.getFormValue('autodetectTargets');
        this.options.colorize = this.getFormValue('colorize');
        const resolution = this.getFormValue('resolution');
        this.options.panoramaResX = resolution.panoramaResX;
        this.options.panoramaResY = resolution.panoramaResY;
        this.options.panoramaRadius = this.getFormValue('radius');
        this.options.maxDistance = this.isEnabledLimitDistance ? this.getFormValue('maxDistanceValue') : -1.0;
        this.options.autodetectSpheres = this.getFormValue('autodetectSpheres');
        this.options.autodetectCheckerboards = this.getFormValue('autodetectCheckerboards');

        const jobAdapterOptions = { ...this.options };
        jobAdapterOptions.autodetectTargets = false;
        // If autodetectTargets is false, reset the relative options
        if (!this.options.autodetectTargets) {
            jobAdapterOptions.autodetectCheckerboards = false;
            jobAdapterOptions.autodetectSpheres = false;
        }
        if (!jobAdapterOptions.autodetectSpheres) {
            jobAdapterOptions.sphereSizes = [];
        }
        return jobAdapterOptions;
    }

    private get fromImport(): boolean {
        return !this.listItems.length;
    }

    private get workspaceStationTypes(): PcvizWorkspaceStationTypes {
        return this.scanProcessingOptionsService.workspaceStationTypes;
    }
}
